import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Card, CardTitle } from "@/components/Card";
import type { DocumentTemplateView, PhaseOutputDocument } from "@/lib/types";
import { conditionNames, predicateFields } from "@/lib/phaseVocabulary";
import {
  BoolField,
  FieldRow,
  RowField,
  RowListField,
  RowTextInput,
  SelectField,
} from "@/components/strategy/FieldEditors";
import PredicateEditor from "@/components/strategy/PredicateEditor";
import { usePhaseEditor, type PhaseEditorProps } from "@/components/strategy/phaseEditing";

/** The output types the engine can produce; extending this is a code change. */
const OUTPUT_TYPES = [{ value: "pdf", label: "PDF document" }];

interface ContextRow {
  key: string;
  value: unknown;
}

/**
 * The document a phase drafts on entry — a HITL-gated draft from one of the
 * strategy's document templates, sent as the next outbound once approved.
 * The whole `output` block is one field: every control rewrites it.
 */
export default function PhaseOutputCard({
  strategyId,
  ...props
}: PhaseEditorProps & { strategyId: string | null }) {
  const { phase, read, edit, isEdited } = usePhaseEditor(props);
  const raw = read(["output"]);
  const output = typeof raw === "object" && raw !== null ? (raw as PhaseOutputDocument) : null;

  const templatesQ = useQuery({
    queryKey: ["document-templates", strategyId],
    enabled: !!strategyId && output !== null,
    queryFn: () =>
      api.get<DocumentTemplateView[]>(`/api/v1/strategies/${strategyId}/document-templates`),
  });
  const templates = templatesQ.data ?? [];
  const templateOptions = templates.map((t) => ({ value: t.slug, label: `${t.name} (${t.slug})` }));
  const templateKnown =
    !templatesQ.data || !output?.template || templates.some((t) => t.slug === output.template);

  const commit = (next: PhaseOutputDocument) => edit(["output"], next);

  const contextRows: ContextRow[] = Object.entries(output?.deal_context ?? {}).map(
    ([key, value]) => ({ key, value }),
  );
  const commitContext = (rows: ContextRow[] | null) => {
    if (!output) return;
    const next = { ...output };
    const entries = (rows ?? []).filter((r) => r.key.trim() !== "");
    if (entries.length === 0) delete next.deal_context;
    else next.deal_context = Object.fromEntries(entries.map((r) => [r.key.trim(), r.value]));
    commit(next);
  };

  return (
    <Card>
      <div className="flex items-center gap-1.5">
        <CardTitle>Document on phase entry</CardTitle>
        {isEdited(["output"]) ? (
          <span title="unsaved change" className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500" />
        ) : null}
      </div>
      <BoolField
        label="draft a document when a prospect enters this phase"
        value={output !== null}
        onCommit={(on) =>
          edit(["output"], on ? { type: "pdf", template: templates[0]?.slug ?? "" } : null)
        }
      />
      {output ? (
        <div className="mt-3 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <FieldRow label="Type">
              <SelectField
                ariaLabel="Document type"
                value={output.type ?? "pdf"}
                options={OUTPUT_TYPES}
                onCommit={(v) => commit({ ...output, type: v ?? "pdf" })}
              />
            </FieldRow>
            <FieldRow
              label="Template"
              hint={
                templatesQ.isError
                  ? "Could not load the document templates."
                  : "From this strategy's Documents page."
              }
            >
              <SelectField
                ariaLabel="Document template"
                value={output.template ?? null}
                options={
                  templateKnown || !output.template
                    ? templateOptions
                    : [{ value: output.template, label: `${output.template} (not found)` }, ...templateOptions]
                }
                allowEmpty="(choose)"
                onCommit={(v) => commit({ ...output, template: v ?? "" })}
              />
              {!templateKnown ? (
                <span className="text-[10px] text-red-700">
                  No document template with this slug exists in the strategy.
                </span>
              ) : null}
            </FieldRow>
          </div>

          <BoolField
            label="send the approved document as the next email"
            value={output.attach_to_email !== false}
            onCommit={(v) => {
              const next = { ...output };
              if (v) delete next.attach_to_email;
              else next.attach_to_email = false;
              commit(next);
            }}
          />

          <FieldRow label="Only when" hint="Empty means every time a prospect enters the phase.">
            <PredicateEditor
              label="Document condition"
              value={output.when ?? null}
              fields={predicateFields(props.document)}
              conditionNames={conditionNames(phase)}
              onChange={(when) => {
                const next = { ...output };
                if (when === null) delete next.when;
                else next.when = when;
                commit(next);
              }}
            />
          </FieldRow>

          <FieldRow
            label="Baseline values"
            hint="Fixed values merged into the drafting context next to the prospect's fields, for terms that are the same for every prospect."
          >
            <RowListField<ContextRow>
              value={contextRows.length === 0 ? null : contextRows}
              rowLabel="baseline value"
              addLabel="Add value"
              emptyText="none"
              newRow={() => ({ key: "", value: "" })}
              onCommit={commitContext}
              renderRow={(row, update, i) => (
                <div className="grid grid-cols-[1fr_2fr] gap-2">
                  <RowField label="key">
                    <RowTextInput
                      ariaLabel={`Baseline value ${i + 1} key`}
                      value={row.key}
                      mono
                      onCommit={(v) => update({ ...row, key: v })}
                    />
                  </RowField>
                  <RowField label="value">
                    <RowTextInput
                      ariaLabel={`Baseline value ${i + 1} value`}
                      value={displayValue(row.value)}
                      onCommit={(v) => update({ ...row, value: parseValue(v) })}
                    />
                  </RowField>
                </div>
              )}
            />
          </FieldRow>
        </div>
      ) : null}
    </Card>
  );
}

function displayValue(v: unknown): string {
  if (v === null || v === undefined) return "";
  return typeof v === "object" ? JSON.stringify(v) : String(v);
}

/** Numbers and booleans keep their type; structured JSON is accepted; else text. */
function parseValue(raw: string): unknown {
  const t = raw.trim();
  if (t === "true") return true;
  if (t === "false") return false;
  if (/^-?\d+(\.\d+)?$/.test(t)) return Number(t);
  if (t.startsWith("{") || t.startsWith("[")) {
    try {
      return JSON.parse(t);
    } catch {
      return raw;
    }
  }
  return raw;
}
